import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import { waitForJob } from '@/api/jobs';
import type { Website } from '@/api/types';
import { useSession } from '@/app/session';
import { Button, Icon, useToast } from '@/design-system';
import { themeQuery, type ThemeOut } from '@/features/designer/api';
import { cx } from '@/lib/cx';
import { errorText } from '@/shell/errorText';
import {
  handoff,
  importCounts,
  latestSync,
  mappingKey,
  mappingQuery,
  orgStoreQuery,
  refreshStore,
  saveMapping,
  storeSyncsQuery,
  syncTheme,
  type MappingRow,
  type OrganizationStore,
  type StoreSync,
} from './api';
import { ImportModal } from './ImportModal';
import s from './store.module.css';

const NEXT_ACTION: Record<MappingRow['action'], MappingRow['action']> = { create: 'update', update: 'skip', skip: 'create' };
const PASSED: Website['status'][] = ['pending_production', 'production'];

/**
 * /sites/:id/sync — «Store Sync» (DESIGN_SPEC §4.5): the approved `theme.json` with «PATCH тему в Laravel»,
 * the organization's store (id, theme versions, the last sync) with «Следующий этап» (the handoff), the
 * editable catalog mapping with «Импорт каталога» (the import modal) and the last import's counters.
 */
export function StoreSyncTab() {
  const site = useOutletContext<Website>();
  const orgId = site.organization.id;
  const theme = useQuery(themeQuery(site.id));
  const store = useQuery(orgStoreQuery(orgId));
  const syncs = useQuery(storeSyncsQuery(site.id));
  const [importing, setImporting] = useState(false);
  const counts = importCounts(latestSync(syncs.data, 'catalog_import'));
  return (
    <div className={s.grid}>
      <ThemeCard site={site} theme={theme.data ?? null} store={store.data ?? null} />
      <div className={s.col}>
        <StoreCard site={site} theme={theme.data ?? null} store={store.data ?? null} latest={syncs.data?.[0] ?? null} />
        <MappingCard orgId={orgId} onImport={() => setImporting(true)} />
        <Counters counts={counts} />
      </div>
      <ImportModal siteId={site.id} orgId={orgId} open={importing} onClose={() => setImporting(false)} />
    </div>
  );
}

function ThemeCard({ site, theme, store }: { site: Website; theme: ThemeOut | null; store: OrganizationStore | null }) {
  const { t } = useTranslation('store');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const push = () => {
    if (!theme || busy) return;
    if (!store) return toast(t('theme.no_store'));
    if (theme.synced_version === theme.version) return toast(t('theme.already', { version: theme.version }));
    setBusy(true);
    syncTheme(site.id)
      .then((r) => {
        toast(t('theme.started', { version: theme.version }));
        return waitForJob(r.job_id).then(() => toast(t('theme.done', { store: store.external_store_id, version: theme.version })));
      })
      .catch((e: unknown) => toast(errorText(e, tc)))
      .finally(() => {
        setBusy(false);
        refreshStore(qc, site.id, site.organization.id);
      });
  };
  return (
    <section className={s.theme} aria-labelledby="theme-json">
      <div className={s.themeHead}>
        <span id="theme-json" className={s.themeTitle}>
          {t('theme.title')}
        </span>
        <span className={s.themeHint}>{t('theme.hint')}</span>
      </div>
      {theme ? <pre className={s.json}>{JSON.stringify(theme.theme, null, 2)}</pre> : <span className={s.themeEmpty}>{t('theme.empty')}</span>}
      <button type="button" className={s.patch} aria-disabled={!theme || busy || undefined} aria-busy={busy} onClick={push}>
        {t('theme.patch')}
        <Icon name="arrow-up-right" size={15} />
      </button>
    </section>
  );
}

function StoreCard({ site, theme, store, latest }: { site: Website; theme: ThemeOut | null; store: OrganizationStore | null; latest: StoreSync | null }) {
  const { t } = useTranslation('store');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const [busy, setBusy] = useState(false);
  const dash = tc('dash');
  const themeValue = !theme
    ? dash
    : theme.synced_version !== null && theme.synced_version !== theme.version
      ? t('card.theme_value', { from: theme.synced_version, to: theme.version })
      : t('card.theme_same', { version: theme.version });
  const status = !latest ? 'none' : latest.status === 'running' ? 'running' : latest.status === 'failed' ? 'failed' : 'synced';
  const tone = { synced: s.statusOk, running: s.statusWarn, failed: s.statusErr, none: s.statusMute }[status];
  const passed = PASSED.includes(site.status);
  const next = () => {
    if (busy) return;
    if (passed) return toast(t('card.handoff_passed'));
    setBusy(true);
    handoff(site.id)
      .then((r) => {
        toast(site.type === 'landing' ? t('card.handoff_landing') : t('card.handoff_done'));
        refreshStore(qc, site.id, site.organization.id);
        if (!r.job_id) return;
        return waitForJob(r.job_id).then(() => {
          refreshStore(qc, site.id, site.organization.id);
          const id = r.store?.external_store_id ?? store?.external_store_id;
          if (id) toast(t('card.provisioned', { store: id }));
        });
      })
      .catch((e: unknown) => toast(errorText(e, tc)))
      .finally(() => setBusy(false));
  };
  return (
    <dl className={s.store} aria-label={t('card.label')}>
      <div className={s.cell}>
        <dt className={s.cellLabel}>{t('card.store_id')}</dt>
        {store ? <dd className={s.cellValue}>{store.external_store_id}</dd> : <dd className={s.cellMuted}>{t('card.no_store')}</dd>}
      </div>
      <div className={s.cell}>
        <dt className={s.cellLabel}>{t('card.theme')}</dt>
        <dd className={s.cellValue}>{themeValue}</dd>
      </div>
      <div className={s.cell}>
        <dt className={s.cellLabel}>{t('card.status')}</dt>
        <dd className={cx(s.status, tone)}>{t(`card.${status}`)}</dd>
      </div>
      {/* «Следующий этап» (TZ §10, additive): the handoff report, then the organization's store. */}
      {can('store:sync') && (
        <div className={s.handoffCell}>
          <Button
            size={34}
            variant="light"
            trailingIcon="arrow-right"
            iconSize={14}
            aria-busy={busy}
            aria-disabled={passed || undefined}
            title={passed ? t('card.handoff_passed') : t('card.handoff_title')}
            onClick={next}
          >
            {t('card.handoff')}
          </Button>
        </div>
      )}
    </dl>
  );
}

function MappingCard({ orgId, onImport }: { orgId: string; onImport: () => void }) {
  const { t } = useTranslation('store');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery(mappingQuery(orgId));
  const rows = q.data ?? [];
  const save = (next: MappingRow[]) => {
    const before = q.data;
    qc.setQueryData<MappingRow[]>(mappingKey(orgId), next);
    saveMapping(
      orgId,
      next.map(({ category, action, laravel_category_ref }) => ({ category, action, laravel_category_ref })),
    )
      .then((saved) => {
        qc.setQueryData<MappingRow[]>(mappingKey(orgId), saved);
        toast(t('mapping.saved'));
      })
      .catch((e: unknown) => {
        qc.setQueryData<MappingRow[]>(mappingKey(orgId), before);
        toast(errorText(e, tc));
      });
  };
  const change = (row: MappingRow, patch: Partial<MappingRow>) => save(rows.map((r) => (r.category === row.category ? { ...r, ...patch } : r)));
  return (
    <section className={s.mapping} aria-labelledby="catalog-mapping">
      <div className={s.mappingHead}>
        <span id="catalog-mapping" className={s.mappingTitle}>
          {t('mapping.title')}
        </span>
        <button type="button" className={s.import} onClick={onImport}>
          <Icon name="upload" size={14} />
          {t('mapping.import')}
        </button>
      </div>
      <div className={s.cols} aria-hidden>
        <span>{t('mapping.studio')}</span>
        <span>{t('mapping.laravel')}</span>
        <span>{t('mapping.count')}</span>
        <span>{t('mapping.action')}</span>
      </div>
      {q.isSuccess && rows.length === 0 ? (
        <div className={s.mappingEmpty}>{t('mapping.empty')}</div>
      ) : (
        <ul className={s.rows}>
          {rows.map((r) => (
            <li key={r.category} className={s.row}>
              <span>{r.name}</span>
              {r.action === 'skip' ? (
                <span className={s.target}>{t('mapping.skip_target')}</span>
              ) : (
                <span className={s.target}>
                  <RefInput row={r} label={t('mapping.ref_label', { name: r.name })} onSave={(ref) => change(r, { laravel_category_ref: ref })} />
                </span>
              )}
              <span className={s.qty}>{r.products}</span>
              <button
                type="button"
                className={cx(s.action, s[r.action])}
                aria-label={t('mapping.action_label', { name: r.name, action: r.action })}
                title={t('mapping.action_label', { name: r.name, action: r.action })}
                onClick={() => change(r, { action: NEXT_ACTION[r.action] })}
              >
                {r.action}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The Laravel category, edited in place: it looks like the design's text until hovered or focused. */
function RefInput({ row, label, onSave }: { row: MappingRow; label: string; onSave: (ref: string | null) => void }) {
  const shown = row.laravel_category_ref ?? row.name;
  const [value, setValue] = useState(shown);
  const [prev, setPrev] = useState(shown);
  if (prev !== shown) {
    setPrev(shown);
    setValue(shown);
  }
  const commit = () => {
    const v = value.trim();
    if (v === shown) return;
    onSave(v || null);
  };
  return (
    <input
      className={s.refInput}
      value={value}
      aria-label={label}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') setValue(shown);
      }}
    />
  );
}

function Counters({ counts }: { counts: ReturnType<typeof importCounts> }) {
  const { t } = useTranslation('store');
  const dash = useTranslation('common').t('dash');
  const cells: [string, number | null, boolean][] = [
    ['created', counts?.created ?? null, false],
    ['updated', counts?.updated ?? null, false],
    ['skipped', counts?.skipped ?? null, false],
    ['errors', counts ? counts.errors.length : null, true],
  ];
  return (
    <dl className={s.counts} aria-label={t('counts.label')}>
      {cells.map(([k, v, err]) => (
        <div key={k} className={s.count}>
          <dt className={s.countLabel}>{t(`counts.${k}` as 'counts.created')}</dt>
          <dd className={cx(s.countValue, err && !!v && s.countErr)}>{v ?? dash}</dd>
        </div>
      ))}
    </dl>
  );
}
